<script setup>
/* eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text, no-console, vue/require-default-prop, no-use-before-define */
import StorageAnalysis from './StorageAnalysis.vue';
import FolderStyleMigration from './FolderStyleMigration.vue';
import { ref, reactive, onMounted, watch, computed, onUnmounted } from 'vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';

// --- S3 state (hoisted to avoid use-before-define lint issues) ---
const s3Config = reactive({
  s3_bucket_name: '',
  s3_access_key_id: '',
  s3_secret_access_key: '',
  s3_region: '',
  s3_endpoint: '',
  s3_force_path_style: false,
  folder_style: false,
});
const showS3Form = ref(false);
const isSavingS3 = ref(false);
const isTestingS3 = ref(false);
const s3TestResult = ref(null);
const confirmationDialogRef = ref(null);
const confirmationDialogState = reactive({
  title: '',
  previousSelection: '',
  newSelection: '',
  impactItems: [],
  confirmActionText: '',
});
let resolveConfirmationDialog = null;

// Função para limpar configuração S3
// --- Funções e estado exclusivos do bloco S3 Storage ---
const onClearS3Config = async () => {
  Object.assign(s3Config, {
    s3_bucket_name: '',
    s3_access_key_id: '',
    s3_secret_access_key: '',
    s3_region: '',
    s3_endpoint: '',
    s3_force_path_style: false,
    folder_style: false,
  });
  try {
    await InboxStorageConfigAPI.updateStorageConfig(inboxId, {
      inbox_storage_config: {
        storage_type: 's3',
        s3_bucket_name: '',
        s3_access_key_id: '',
        s3_secret_access_key: '',
        s3_region: '',
        s3_endpoint: '',
        s3_force_path_style: false,
        folder_style: false,
      },
    });
    s3TestResult.value = { success: true, message: 'Configuração S3 limpa.' };
  } catch (e) {
    s3TestResult.value = {
      success: false,
      message: 'Erro ao limpar configuração.',
    };
  }
};

// Função para cancelar edição (apenas reseta o form visualmente)
const onCancelS3Config = () => {
  fetchConfig();
  s3TestResult.value = null;
};

import { useI18n } from 'vue-i18n';
import axios from 'axios';
import InboxStorageConfigAPI from 'dashboard/api/inboxStorageConfig';

const { t } = useI18n();
import { useRoute } from 'vue-router';

// Utilitário para mapear status salvo para badge
// --- Utilitário exclusivo do bloco S3 Storage ---
function getS3TestResultFromApi(config) {
  if (!config) return null;
  if (config.last_test_status === 'success') {
    return { success: true, message: config.last_test_message };
  }
  if (config.last_test_status === 'error') {
    return { success: false, message: config.last_test_message };
  }
  // pending ou null
  return null;
}

// --- Utilitário exclusivo do bloco Default Storage (ENV) ---
// getDefaultTestResultFromApi removed: currently unused helper

const route = useRoute();
const inboxId = route.params.inboxId;
const selectedType = ref('from_account'); // será atualizado pelo fetchConfig
const lastSavedType = ref('from_account');
const lastSavedFolderStyle = ref(false);
const envStorageConfig = ref(null);
// --- Funções e estado exclusivos do bloco Default Storage (ENV) ---
const isTestingDefaultS3 = ref(false);
const defaultS3TestResult = ref(null);
// Cache do resultado do teste Default S3 (apenas frontend)
let defaultS3TestResultCache = null;
const showDefaultS3TestButton = computed(() => {
  return envStorageConfig.value?.resolved_storage_type === 's3';
});

// Executa teste automático do Default S3 se necessário
const maybeAutoTestDefaultS3 = async () => {
  if (
    selectedType.value === 'from_account' &&
    envStorageConfig.value &&
    envStorageConfig.value.resolved_storage_type === 's3' &&
    !defaultS3TestResultCache
  ) {
    isTestingDefaultS3.value = true;
    try {
      const payload = { use_inherited: true };
      const { data } = await InboxStorageConfigAPI.testS3Config(
        inboxId,
        payload
      );
      let msg = data.message;
      if (
        data.success &&
        (!msg || msg === 'Configuração S3 salva com sucesso!')
      ) {
        msg = t('INBOX_MGMT.STORAGE_SETTINGS.S3_CONNECTION_SUCCESS');
      }
      defaultS3TestResultCache = {
        success: data.success,
        message: msg,
        detail: data.detail,
      };
      defaultS3TestResult.value = defaultS3TestResultCache;
    } catch (e) {
      let msg = 'Erro ao testar conexão S3 herdada.';
      let detail = '';
      if (e.response && e.response.data) {
        if (e.response.data.message) {
          msg = e.response.data.message;
        }
        if (e.response.data.detail) {
          detail = e.response.data.detail;
        }
        if (!msg && typeof e.response.data === 'string') {
          msg = e.response.data;
        } else if (!msg && e.response.data.errors) {
          msg = Array.isArray(e.response.data.errors)
            ? e.response.data.errors.join(', ')
            : e.response.data.errors;
        }
      } else if (e.message) {
        msg = e.message;
      }
      defaultS3TestResultCache = { success: false, message: msg, detail };
      defaultS3TestResult.value = defaultS3TestResultCache;
    } finally {
      isTestingDefaultS3.value = false;
    }
  } else if (
    selectedType.value === 'from_account' &&
    defaultS3TestResultCache
  ) {
    defaultS3TestResult.value = defaultS3TestResultCache;
  }
};
// Função exclusiva para teste temporário do From Account, sem persistência
const onTestInheritedS3Config = async () => {
  isTestingDefaultS3.value = true;
  defaultS3TestResult.value = null;
  try {
    const payload = { use_inherited: true, force: true };
    const { data } = await InboxStorageConfigAPI.testS3Config(inboxId, payload);
    let msg = data.message;
    if (
      data.success &&
      (!msg || msg === 'Configuração S3 salva com sucesso!')
    ) {
      msg = 'Conexão S3 herdada bem-sucedida!';
    }
    defaultS3TestResultCache = {
      success: data.success,
      message: msg,
      detail: data.detail,
    };
    defaultS3TestResult.value = defaultS3TestResultCache;
  } catch (e) {
    let msg = 'Erro ao testar conexão S3 herdada.';
    let detail = '';
    if (e.response && e.response.data) {
      if (e.response.data.message) {
        msg = e.response.data.message;
      }
      if (e.response.data.detail) {
        detail = e.response.data.detail;
      }
      if (!msg && typeof e.response.data === 'string') {
        msg = e.response.data;
      } else if (!msg && e.response.data.errors) {
        msg = Array.isArray(e.response.data.errors)
          ? e.response.data.errors.join(', ')
          : e.response.data.errors;
      }
    } else if (e.message) {
      msg = e.message;
    }
    defaultS3TestResultCache = { success: false, message: msg, detail };
    defaultS3TestResult.value = defaultS3TestResultCache;
  } finally {
    isTestingDefaultS3.value = false;
  }
};
// --- Fim do bloco Default Storage (ENV) isolado ---

const fetchEnvStorageConfig = async () => {
  try {
    const { data } = await InboxStorageConfigAPI.getEnvStorageConfig(inboxId);
    envStorageConfig.value = data;
    await maybeAutoTestDefaultS3();
  } catch (e) {
    envStorageConfig.value = null;
  }
};

const fetchConfig = async () => {
  try {
    const { data } = await InboxStorageConfigAPI.getStorageConfig(inboxId);
    selectedType.value = data.storage_type || 'from_account';
    lastSavedType.value = selectedType.value;
    // Always map folder_style from the stored config, even when storage_type is 'from_account'
    Object.assign(s3Config, {
      folder_style: data.folder_style || false,
    });
    lastSavedFolderStyle.value = Boolean(data.folder_style);

    if (selectedType.value === 's3') {
      Object.assign(s3Config, {
        s3_bucket_name: data.s3_bucket_name || '',
        s3_access_key_id: data.s3_access_key_id || '',
        s3_secret_access_key: data.s3_secret_access_key || '',
        s3_region: data.s3_region || '',
        s3_endpoint: data.s3_endpoint || '',
        s3_force_path_style: data.s3_force_path_style || false,
      });
      s3TestResult.value = getS3TestResultFromApi(data);
      showS3Form.value =
        !s3Config.s3_bucket_name ||
        !s3Config.s3_access_key_id ||
        !s3Config.s3_secret_access_key ||
        !s3Config.s3_region;
    } else {
      showS3Form.value = false;
    }
    // ENV config só para exibir info, nunca para decidir storage ativo
    await fetchEnvStorageConfig();
  } catch (e) {
    selectedType.value = 'from_account';
    lastSavedType.value = 'from_account';
    lastSavedFolderStyle.value = false;
    showS3Form.value = false;
    await fetchEnvStorageConfig();
  }
};

const STORAGE_TYPE_ORDER = ['from_account', 'local', 's3'];
const NEW_FOLDER_STYLE_PATH_EXAMPLE =
  'AC_00001/INB_00001/CON_288_12345/file.ext';
const OLD_FOLDER_STYLE_PATH_EXAMPLE = 'arquivo.ext (na raiz do bucket S3)';

const storageTypeLabel = type => {
  if (type === 'from_account')
    return t('INBOX_MGMT.STORAGE_SETTINGS.FROM_ACCOUNT');
  if (type === 'local') return t('INBOX_MGMT.STORAGE_ANALYSIS.LOCAL');
  return t('INBOX_MGMT.STORAGE_ANALYSIS.S3_STORAGE');
};

const storageTypePosition = type => {
  const idx = STORAGE_TYPE_ORDER.indexOf(type);
  return idx >= 0 ? idx + 1 : 0;
};

const storageTypeImpact = type => {
  if (type === 'from_account')
    return t('INBOX_MGMT.STORAGE_SETTINGS.IMPACT_FROM_ACCOUNT');
  if (type === 'local') return t('INBOX_MGMT.STORAGE_SETTINGS.IMPACT_LOCAL');
  return t('INBOX_MGMT.STORAGE_SETTINGS.IMPACT_S3');
};

const openConfirmationDialog = ({
  title,
  previousSelection,
  newSelection,
  impactItems,
  confirmActionText = '',
}) => {
  confirmationDialogState.title = title;
  confirmationDialogState.previousSelection = previousSelection;
  confirmationDialogState.newSelection = newSelection;
  confirmationDialogState.impactItems = impactItems;
  confirmationDialogState.confirmActionText = confirmActionText;

  return new Promise(resolve => {
    resolveConfirmationDialog = resolve;
    confirmationDialogRef.value?.open();
  });
};

const resolveConfirmation = result => {
  if (!resolveConfirmationDialog) return;
  const resolve = resolveConfirmationDialog;
  resolveConfirmationDialog = null;
  resolve(result);
};

const handleConfirmationDialogConfirm = () => {
  resolveConfirmation(true);
  confirmationDialogRef.value?.close();
};

const handleConfirmationDialogClose = () => {
  resolveConfirmation(false);
};

const confirmTypeChange = (previousType, nextType) => {
  return openConfirmationDialog({
    title: t('INBOX_MGMT.STORAGE_SETTINGS.CHANGE_WARNING_TITLE'),
    previousSelection: `${storageTypeLabel(previousType)} (${t('INBOX_MGMT.STORAGE_SETTINGS.POSITION')} ${storageTypePosition(previousType)})`,
    newSelection: `${storageTypeLabel(nextType)} (${t('INBOX_MGMT.STORAGE_SETTINGS.POSITION')} ${storageTypePosition(nextType)})`,
    impactItems: [
      storageTypeImpact(nextType),
      t('INBOX_MGMT.STORAGE_SETTINGS.IMPACT_IMMEDIATE'),
    ],
  });
};

const onTypeChange = async () => {
  const previousType = lastSavedType.value;
  const nextType = selectedType.value;
  if (previousType === nextType) return;

  if (!(await confirmTypeChange(previousType, nextType))) {
    selectedType.value = previousType;
    return;
  }

  // Salva imediatamente o tipo selecionado no banco
  await saveType();
  lastSavedType.value = nextType;
  // Atualiza o estado local após salvar
  await fetchConfig();
  // Limpa o cache do teste Default S3 ao trocar de tipo
  if (selectedType.value !== 'from_account') {
    defaultS3TestResultCache = null;
    defaultS3TestResult.value = null;
  }
};

const showFolderStyleCheckboxAtType = computed(() => {
  return ['from_account', 'local', 's3'].includes(selectedType.value);
});

const analysisCompletedThisSession = ref(false);
const analysisHasLocalFiles = ref(false);

// Compute the effective bucket for this inbox (either explicit inbox S3 or default ENV bucket)
const effectiveBucket = computed(() => {
  if (selectedType.value === 's3' && s3Config.s3_bucket_name)
    return s3Config.s3_bucket_name;
  if (
    selectedType.value === 'from_account' &&
    envStorageConfig.value?.storage_bucket_name
  )
    return envStorageConfig.value.storage_bucket_name;
  return null;
});

// Show the Folder style reorganization section whenever the new folder style is enabled.
// This reorganization flow works for both Local and S3 storages.
const showFolderStyleSection = computed(() => {
  return Boolean(s3Config.folder_style);
});

const showFolderStyleCounters = computed(() => {
  return Boolean(s3Config.folder_style);
});

const showStorageAnalysisSection = computed(() => {
  // Folder-style analysis must be available independently from migration flows.
  // Keep the same visibility criteria for all storage types.
  return ['from_account', 'local', 's3'].includes(selectedType.value);
});

// Listen for analysis completion events from StorageAnalysis component
const onAnalysisDone = e => {
  // Only mark completion for this inbox when the event matches
  if (e?.detail && String(e.detail.inboxId) === String(inboxId)) {
    // If the event carries a bucket, ensure it matches the effective bucket for this inbox
    if (
      e.detail.bucket &&
      String(e.detail.bucket) !== String(effectiveBucket.value)
    )
      return;
    analysisCompletedThisSession.value = true;
    analysisHasLocalFiles.value = Boolean(e.detail.hasLocalFiles);
  }
};
window.addEventListener('storage_analysis:done', onAnalysisDone);

// If the effective bucket or selected type changes, previous analysis in this session may no longer apply
watch(
  () => effectiveBucket.value,
  (newVal, oldVal) => {
    if (newVal !== oldVal) {
      analysisCompletedThisSession.value = false;
      analysisHasLocalFiles.value = false;
    }
  }
);

// Ensure we check cache on mount
const folderStyleStatus = ref('not_started');
const storageMigrationStatus = ref('not_started');
const existenceStatus = ref('not_started');
let folderStyleInterval = null;
let storageMigrationInterval = null;
let currentPollingMode = null;
const ACTIVE_POLL_MS = 5000;
const IDLE_POLL_MS = 30000;
const isActiveMigrationStatus = status =>
  ['running', 'paused'].includes(status);

const startStatusPolling = intervalMs => {
  if (!folderStyleInterval) {
    folderStyleInterval = setInterval(fetchFolderStyleState, intervalMs);
  }
  if (!storageMigrationInterval) {
    storageMigrationInterval = setInterval(
      fetchStorageMigrationState,
      intervalMs
    );
  }
};

const stopStatusPolling = () => {
  if (folderStyleInterval) {
    clearInterval(folderStyleInterval);
    folderStyleInterval = null;
  }
  if (storageMigrationInterval) {
    clearInterval(storageMigrationInterval);
    storageMigrationInterval = null;
  }
};

const syncStatusPolling = () => {
  const shouldUseActivePolling =
    isActiveMigrationStatus(folderStyleStatus.value) ||
    isActiveMigrationStatus(storageMigrationStatus.value);
  const nextMode = shouldUseActivePolling ? 'active' : 'idle';
  if (currentPollingMode === nextMode) return;

  if (
    isActiveMigrationStatus(folderStyleStatus.value) ||
    isActiveMigrationStatus(storageMigrationStatus.value)
  ) {
    stopStatusPolling();
    startStatusPolling(ACTIVE_POLL_MS);
    currentPollingMode = 'active';
    return;
  }

  stopStatusPolling();
  startStatusPolling(IDLE_POLL_MS);
  currentPollingMode = 'idle';
};

const stopAllStatusPolling = () => {
  currentPollingMode = null;
  stopStatusPolling();
};

const syncStatusPollingFast = async () => {
  // quick refresh hook for immediate UI update before syncing interval mode
  await fetchFolderStyleState();
  await fetchStorageMigrationState();
  syncStatusPolling();
};

const syncStatusPollingOnVisibility = () => {
  if (document.visibilityState === 'visible') {
    syncStatusPollingFast();
  }
};

const syncStatusPollingOnFocus = () => {
  syncStatusPollingFast();
};

const syncStatusPollingOnOnline = () => {
  syncStatusPollingFast();
};

const fetchFolderStyleState = async () => {
  try {
    const { data } = await axios.get(
      `/api/v1/inboxes/${inboxId}/folder_style_migrations/${inboxId}/state`
    );
    folderStyleStatus.value = data?.status || 'not_started';
  } catch (e) {
    folderStyleStatus.value = 'not_started';
  }
};
const fetchStorageMigrationState = async () => {
  try {
    const { data } = await axios.get(
      `/api/v1/inboxes/${inboxId}/storage_migrations/${inboxId}/state`
    );
    storageMigrationStatus.value = data?.status || 'not_started';
  } catch (e) {
    storageMigrationStatus.value = 'not_started';
  }
};

// Listen for existence status events broadcast by StorageAnalysis
const onExistenceStatus = e => {
  if (e?.detail && String(e.detail.inboxId) === String(inboxId)) {
    existenceStatus.value = e.detail.status || 'not_started';
  }
};
window.addEventListener('storage_existence:status', onExistenceStatus);

const onFolderStyleMigrationStatus = e => {
  if (e?.detail && String(e.detail.inboxId) === String(inboxId)) {
    folderStyleStatus.value = e.detail.status || 'not_started';
    syncStatusPolling();
  }
};
window.addEventListener(
  'folder_style_migration:status',
  onFolderStyleMigrationStatus
);

const onStorageMigrationStatus = e => {
  if (e?.detail && String(e.detail.inboxId) === String(inboxId)) {
    storageMigrationStatus.value = e.detail.status || 'not_started';
    syncStatusPolling();
  }
};
window.addEventListener('storage_migration:status', onStorageMigrationStatus);

onMounted(async () => {
  await fetchConfig();
  await fetchFolderStyleState();
  await fetchStorageMigrationState();
  // Poll status faster when active, and slower when idle to avoid "frozen" UI.
  syncStatusPolling();
  window.addEventListener('focus', syncStatusPollingOnFocus);
  window.addEventListener('online', syncStatusPollingOnOnline);
  document.addEventListener('visibilitychange', syncStatusPollingOnVisibility);
});

watch(
  [folderStyleStatus, storageMigrationStatus],
  () => {
    syncStatusPolling();
  },
  { deep: false }
);

onUnmounted(() => {
  window.removeEventListener('storage_analysis:done', onAnalysisDone);
  window.removeEventListener('storage_existence:status', onExistenceStatus);
  window.removeEventListener(
    'folder_style_migration:status',
    onFolderStyleMigrationStatus
  );
  window.removeEventListener(
    'storage_migration:status',
    onStorageMigrationStatus
  );
  window.removeEventListener('focus', syncStatusPollingOnFocus);
  window.removeEventListener('online', syncStatusPollingOnOnline);
  document.removeEventListener(
    'visibilitychange',
    syncStatusPollingOnVisibility
  );
  stopAllStatusPolling();
});

const saveType = async () => {
  try {
    await InboxStorageConfigAPI.updateStorageConfig(inboxId, {
      inbox_storage_config: {
        storage_type: selectedType.value,
        folder_style: s3Config.folder_style,
      },
    });
  } catch (e) {
    // non-blocking but log for debugging
    console.error(e);
  }
};

const onFolderStyleChange = async () => {
  const previousState = Boolean(lastSavedFolderStyle.value);
  const nextState = Boolean(s3Config.folder_style);
  if (previousState === nextState) return;

  const confirmed = await openConfirmationDialog({
    title: t('INBOX_MGMT.STORAGE_SETTINGS.CHANGE_WARNING_TITLE'),
    previousSelection: previousState
      ? t('INBOX_MGMT.STORAGE_SETTINGS.YES')
      : t('INBOX_MGMT.STORAGE_SETTINGS.NO'),
    newSelection: nextState
      ? t('INBOX_MGMT.STORAGE_SETTINGS.YES')
      : t('INBOX_MGMT.STORAGE_SETTINGS.NO'),
    impactItems: [
      nextState
        ? t('INBOX_MGMT.STORAGE_SETTINGS.IMPACT_FOLDER_STYLE_ON')
        : t('INBOX_MGMT.STORAGE_SETTINGS.IMPACT_FOLDER_STYLE_OFF'),
      t('INBOX_MGMT.STORAGE_SETTINGS.IMPACT_EXISTING_FILES'),
    ],
    confirmActionText: nextState
      ? t('INBOX_MGMT.STORAGE_SETTINGS.CONFIRM_ACTION_ENABLE_FOLDER_STYLE', {
          newPathExample: NEW_FOLDER_STYLE_PATH_EXAMPLE,
          oldPathExample: OLD_FOLDER_STYLE_PATH_EXAMPLE,
        })
      : t('INBOX_MGMT.STORAGE_SETTINGS.CONFIRM_ACTION_DISABLE_FOLDER_STYLE', {
          newPathExample: NEW_FOLDER_STYLE_PATH_EXAMPLE,
          oldPathExample: OLD_FOLDER_STYLE_PATH_EXAMPLE,
        }),
  });

  if (!confirmed) {
    s3Config.folder_style = previousState;
    return;
  }

  try {
    // Apply the confirmed choice immediately in UI to avoid stale checkbox state.
    s3Config.folder_style = nextState;
    await InboxStorageConfigAPI.updateStorageConfig(inboxId, {
      inbox_storage_config: {
        storage_type: selectedType.value,
        folder_style: nextState,
      },
    });
    lastSavedFolderStyle.value = nextState;
    // Re-sync from backend to keep frontend consistent with persisted value.
    await fetchConfig();
  } catch (e) {
    // non-blocking but log for debugging
    console.error(e);
    s3Config.folder_style = previousState;
    lastSavedFolderStyle.value = previousState;
  }
};

const onTestS3Config = async () => {
  isTestingS3.value = true;
  s3TestResult.value = null;
  try {
    const { data } = await InboxStorageConfigAPI.testS3Config(inboxId, {
      inbox_storage_config: { ...s3Config, storage_type: 's3' },
    });
    let msg = data.message;
    if (
      data.success &&
      (!msg || msg === 'Configuração S3 salva com sucesso!')
    ) {
      msg = t('INBOX_MGMT.STORAGE_SETTINGS.S3_CONNECTION_SUCCESS');
    }
    s3TestResult.value = {
      success: data.success,
      message: msg,
      detail: data.detail,
    };
  } catch (e) {
    let msg = 'Erro ao testar conexão S3.';
    let detail = '';
    if (e.response && e.response.data) {
      if (e.response.data.message) {
        msg = e.response.data.message;
      }
      if (e.response.data.detail) {
        detail = e.response.data.detail;
      }
      if (!msg && typeof e.response.data === 'string') {
        msg = e.response.data;
      } else if (!msg && e.response.data.errors) {
        msg = Array.isArray(e.response.data.errors)
          ? e.response.data.errors.join(', ')
          : e.response.data.errors;
      }
    } else if (e.message) {
      msg = e.message;
    }
    s3TestResult.value = { success: false, message: msg, detail };
  } finally {
    isTestingS3.value = false;
  }
};

const onSaveS3Config = async () => {
  isSavingS3.value = true;
  try {
    await InboxStorageConfigAPI.updateStorageConfig(inboxId, {
      inbox_storage_config: {
        storage_type: 's3',
        ...s3Config,
      },
    });
    showS3Form.value = false;
    // Após salvar, volta para status aguardando teste
    s3TestResult.value = null;
  } catch (e) {
    s3TestResult.value = {
      success: false,
      message: 'Erro ao salvar configuração.',
    };
  } finally {
    isSavingS3.value = false;
  }
};

onMounted(fetchConfig);
// Removido o watch para selectedType: agora tudo é controlado pelo banco e fetchConfig
</script>

<template>
  <div>
    <!-- Storage Type Selection -->
    <div class="ml-0 mr-0 py-8 w-full">
      <div class="grid grid-cols-1 lg:grid-cols-8 gap-6">
        <div class="col-span-2">
          <h3 class="text-lg font-semibold text-n-brand mb-0">
            {{ t('INBOX_MGMT.STORAGE_SETTINGS.STORAGE_TYPE') }}
          </h3>
          <p
            class="text-sm mb-2 text-n-slate-11 leading-5 tracking-normal mt-2"
          >
            {{ t('INBOX_MGMT.STORAGE_SETTINGS.STORAGE_TYPE_DESC') }}
          </p>
        </div>
        <div class="col-span-6 xl:col-span-5">
          <div class="space-y-4">
            <div class="flex items-center gap-6">
              <label class="flex items-center gap-2 cursor-pointer">
                <input
                  v-model="selectedType"
                  type="radio"
                  value="from_account"
                  name="storageType"
                  class="rounded"
                  @change="onTypeChange"
                />
                <span class="text-sm font-medium text-slate-700">{{
                  t('INBOX_MGMT.STORAGE_SETTINGS.FROM_ACCOUNT')
                }}</span>
              </label>
              <label class="flex items-center gap-2 cursor-pointer">
                <input
                  v-model="selectedType"
                  type="radio"
                  value="local"
                  name="storageType"
                  class="rounded"
                  @change="onTypeChange"
                />
                <span class="text-sm font-medium text-slate-700">Local</span>
              </label>
              <label class="flex items-center gap-2 cursor-pointer">
                <input
                  v-model="selectedType"
                  type="radio"
                  value="s3"
                  name="storageType"
                  class="rounded"
                  @change="onTypeChange"
                />
                <span class="text-sm font-medium text-slate-700"
                  >S3 Storage</span
                >
              </label>
            </div>
            <div v-if="showFolderStyleCheckboxAtType" class="mt-3">
              <label class="flex items-center gap-2 cursor-pointer">
                <input
                  id="new-folder-style-type"
                  v-model="s3Config.folder_style"
                  type="checkbox"
                  class="rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                  @change="onFolderStyleChange"
                />
                <span
                  class="inline-flex items-center gap-1.5 text-sm font-medium text-slate-700"
                >
                  {{ t('INBOX_MGMT.STORAGE_SETTINGS.NEW_IBOX_FOLDER_STYLE') }}
                  <button
                    v-tooltip="{
                      placement: 'bottom',
                      theme: 'message-tooltip',
                      content: t(
                        'INBOX_MGMT.STORAGE_SETTINGS.NEW_IBOX_FOLDER_STYLE_TOOLTIP'
                      ),
                      delay: { show: 150, hide: 0 },
                      triggers: ['hover', 'focus', 'click'],
                    }"
                    type="button"
                    :aria-label="
                      t(
                        'INBOX_MGMT.STORAGE_SETTINGS.NEW_IBOX_FOLDER_STYLE_TOOLTIP'
                      )
                    "
                    class="inline-flex items-center text-slate-500 cursor-help"
                    @click.stop.prevent
                  >
                    <i class="i-lucide-info w-4 h-4" />
                  </button>
                </span>
              </label>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- From Account Info Section -->

    <div
      v-if="selectedType === 'from_account' && envStorageConfig"
      class="ml-0 mr-0 py-8 w-full border-b border-solid border-n-weak/60 dark:border-n-weak"
    >
      <div class="grid grid-cols-1 lg:grid-cols-8 gap-6">
        <div class="col-span-2">
          <p class="text-base text-n-brand mb-0 font-medium">
            {{ t('INBOX_MGMT.STORAGE_SETTINGS.FROM_ACCOUNT_STORAGE') }}
          </p>
          <p
            class="text-sm mb-2 text-n-slate-11 leading-5 tracking-normal mt-2"
          >
            {{ t('INBOX_MGMT.STORAGE_SETTINGS.FROM_ACCOUNT_STORAGE_DESC') }}
          </p>
        </div>
        <div class="col-span-6 xl:col-span-5">
          <div class="space-y-4">
            <div
              class="p-4 rounded-md"
              :class="[
                envStorageConfig.resolved_storage_type === 'local'
                  ? 'bg-yellow-100 border border-yellow-400'
                  : defaultS3TestResult && !defaultS3TestResult.success
                    ? 'bg-red-50 border border-red-400'
                    : defaultS3TestResult && defaultS3TestResult.success
                      ? 'bg-green-50 border border-green-200'
                      : 'bg-yellow-100 border border-yellow-400',
              ]"
            >
              <template
                v-if="envStorageConfig.resolved_storage_type === 'local'"
              >
                <div class="flex flex-col gap-2">
                  <div class="text-base font-semibold text-slate-700">
                    Storage Type:
                    <span class="font-bold text-n-brand">Local</span>
                  </div>
                  <div class="text-base text-slate-700">
                    {{
                      envStorageConfig.resolved_source === 'account'
                        ? t('INBOX_MGMT.STORAGE_SETTINGS.USING_ACCOUNT_LOCAL')
                        : t('INBOX_MGMT.STORAGE_SETTINGS.USING_ENV_LOCAL')
                    }}
                  </div>
                </div>
              </template>
              <template
                v-else-if="envStorageConfig.resolved_storage_type === 's3'"
              >
                <div class="flex flex-col gap-2 mb-4">
                  <div class="text-base font-semibold text-slate-700">
                    Storage Type:
                    <span class="font-bold text-n-brand">S3 Storage</span>
                  </div>
                  <div class="text-base text-slate-700">
                    {{
                      envStorageConfig.resolved_source === 'account'
                        ? t('INBOX_MGMT.STORAGE_SETTINGS.USING_ACCOUNT_S3')
                        : t('INBOX_MGMT.STORAGE_SETTINGS.USING_ENV_S3')
                    }}
                  </div>
                </div>
                <div class="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span class="font-medium text-slate-700">Bucket:</span>
                    <span class="ml-2 text-slate-900">{{
                      envStorageConfig.storage_bucket_name
                    }}</span>
                  </div>
                  <div>
                    <span class="font-medium text-slate-700">Region:</span>
                    <span class="ml-2 text-slate-900">{{
                      envStorageConfig.storage_region
                    }}</span>
                  </div>
                  <div class="col-span-2">
                    <span class="font-medium text-slate-700">Endpoint:</span>
                    <span class="ml-2 text-slate-900 break-all">{{
                      envStorageConfig.storage_endpoint
                    }}</span>
                  </div>
                </div>
                <div
                  v-if="showDefaultS3TestButton"
                  class="mt-2 flex flex-col gap-2 md:flex-row md:items-center md:gap-4"
                >
                  <button
                    type="button"
                    :disabled="isTestingDefaultS3"
                    class="inline-flex items-center min-w-0 gap-2 transition-all duration-200 ease-in-out border-0 rounded-lg outline-1 outline disabled:opacity-50 bg-n-brand text-white hover:enabled:brightness-110 focus-visible:brightness-110 outline-transparent h-10 px-4 text-sm font-medium justify-center"
                    @click="onTestInheritedS3Config"
                  >
                    {{ t('INBOX_MGMT.STORAGE_SETTINGS.TEST_S3_CONNECTION') }}
                  </button>
                  <div
                    v-if="defaultS3TestResult"
                    class="flex items-center gap-2"
                  >
                    <span
                      v-if="defaultS3TestResult.success"
                      class="border border-green-500 dark:border-green-400 rounded px-2 py-1 text-xs font-semibold inline-flex items-center gap-1 bg-green-500 text-white dark:bg-green-400 dark:text-green-900"
                    >
                      <svg
                        class="w-3 h-3 mr-1 text-green-500 dark:text-green-300"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                        viewBox="0 0 24 24"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                      {{ defaultS3TestResult.message }}
                    </span>
                    <span
                      v-else
                      class="border border-red-500 dark:border-red-400 rounded px-2 py-1 text-xs font-semibold inline-flex items-center gap-1 bg-red-500 text-white dark:bg-red-400 dark:text-red-900"
                    >
                      <svg
                        class="w-3 h-3 mr-1 text-red-100 dark:text-red-900"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                        viewBox="0 0 24 24"
                      >
                        <circle cx="12" cy="12" r="10" />
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          d="M12 8v4m0 4h.01"
                        />
                      </svg>
                      {{ defaultS3TestResult.message }}
                    </span>
                  </div>
                </div>
              </template>
              <template v-else>
                <div
                  v-if="showDefaultS3TestButton"
                  class="flex items-center justify-between mb-2"
                >
                  <p
                    :class="[
                      defaultS3TestResult && !defaultS3TestResult.success
                        ? 'text-red-900'
                        : defaultS3TestResult && defaultS3TestResult.success
                          ? 'text-green-900'
                          : 'text-yellow-900',
                    ]"
                    class="text-sm font-medium"
                  >
                    {{
                      t(
                        'INBOX_MGMT.STORAGE_SETTINGS.CURRENT_FROM_ACCOUNT_CONFIGURATION'
                      )
                    }}
                  </p>
                  <button
                    type="button"
                    :disabled="isTestingDefaultS3"
                    class="inline-flex items-center min-w-0 gap-2 transition-all duration-200 ease-in-out border-0 rounded-lg outline-1 outline disabled:opacity-50 bg-n-brand text-white hover:enabled:brightness-110 focus-visible:brightness-110 outline-transparent h-10 px-4 text-sm font-medium justify-center"
                    @click="onTestInheritedS3Config"
                  >
                    {{ t('INBOX_MGMT.STORAGE_SETTINGS.TEST_S3_CONNECTION') }}
                  </button>
                </div>
                <div class="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span
                      :class="[
                        defaultS3TestResult && !defaultS3TestResult.success
                          ? 'text-red-700'
                          : defaultS3TestResult && defaultS3TestResult.success
                            ? 'text-green-700'
                            : 'text-yellow-700',
                      ]"
                      class="font-medium"
                      >{{
                        t('INBOX_MGMT.STORAGE_SETTINGS.BUCKET') || 'Bucket:'
                      }}</span
                    >
                    <span
                      :class="[
                        defaultS3TestResult && !defaultS3TestResult.success
                          ? 'text-red-900'
                          : defaultS3TestResult && defaultS3TestResult.success
                            ? 'text-green-900'
                            : 'text-yellow-900',
                      ]"
                      class="ml-2"
                      >{{ envStorageConfig.storage_bucket_name }}</span
                    >
                  </div>
                  <div>
                    <span
                      :class="[
                        defaultS3TestResult && !defaultS3TestResult.success
                          ? 'text-red-700'
                          : defaultS3TestResult && defaultS3TestResult.success
                            ? 'text-green-700'
                            : 'text-yellow-700',
                      ]"
                      class="font-medium"
                      >Region:</span
                    >
                    <span
                      :class="[
                        defaultS3TestResult && !defaultS3TestResult.success
                          ? 'text-red-900'
                          : defaultS3TestResult && defaultS3TestResult.success
                            ? 'text-green-900'
                            : 'text-yellow-900',
                      ]"
                      class="ml-2"
                      >{{ envStorageConfig.storage_region }}</span
                    >
                  </div>
                  <div class="col-span-2">
                    <span
                      :class="[
                        defaultS3TestResult && !defaultS3TestResult.success
                          ? 'text-red-700'
                          : defaultS3TestResult && defaultS3TestResult.success
                            ? 'text-green-700'
                            : 'text-yellow-700',
                      ]"
                      class="font-medium"
                      >Endpoint:</span
                    >
                    <span
                      :class="[
                        defaultS3TestResult && !defaultS3TestResult.success
                          ? 'text-red-900'
                          : defaultS3TestResult && defaultS3TestResult.success
                            ? 'text-green-900'
                            : 'text-yellow-900',
                      ]"
                      class="ml-2 break-all"
                      >{{ envStorageConfig.storage_endpoint }}</span
                    >
                  </div>
                </div>
                <div v-if="defaultS3TestResult" class="mt-2">
                  <div class="flex items-center gap-2">
                    <span
                      v-if="defaultS3TestResult.success"
                      class="border border-green-500 dark:border-green-400 rounded px-2 py-1 text-xs font-semibold inline-flex items-center gap-1 bg-green-500 text-white dark:bg-green-400 dark:text-green-900"
                    >
                      <svg
                        class="w-3 h-3 mr-1 text-green-500 dark:text-green-300"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                        viewBox="0 0 24 24"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                      {{ defaultS3TestResult.message }}
                    </span>
                    <span
                      v-else
                      class="border border-red-500 dark:border-red-400 rounded px-2 py-1 text-xs font-semibold inline-flex items-center gap-1 bg-red-500 text-white dark:bg-red-400 dark:text-red-900"
                    >
                      <svg
                        class="w-3 h-3 mr-1 text-red-100 dark:text-red-900"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                        viewBox="0 0 24 24"
                      >
                        <circle cx="12" cy="12" r="10" />
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          d="M12 8v4m0 4h.01"
                        />
                      </svg>
                      {{ defaultS3TestResult.message }}
                    </span>
                  </div>
                </div>
              </template>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- S3 Configuration Section -->
    <div
      v-if="selectedType === 's3'"
      class="ml-0 mr-0 py-8 w-full border-b border-solid border-n-weak/60 dark:border-n-weak"
    >
      <div class="grid grid-cols-1 lg:grid-cols-8 gap-6">
        <div class="col-span-2">
          <h3 class="text-lg font-semibold text-n-brand mb-0">
            S3 Configuration
          </h3>
          <p
            class="text-sm mb-2 text-n-slate-11 leading-5 tracking-normal mt-2"
          >
            Configure S3-compatible storage for this inbox
          </p>
        </div>
        <div class="col-span-6 xl:col-span-5">
          <div class="space-y-4">
            <!-- Bloco de configuração salva/erro -->
            <div
              v-if="
                !showS3Form &&
                s3Config.s3_bucket_name &&
                s3Config.s3_access_key_id &&
                s3Config.s3_secret_access_key &&
                s3Config.s3_region
              "
              class="p-4 rounded-md"
              :class="[
                s3TestResult && !s3TestResult.success
                  ? 'bg-red-50 border border-red-400'
                  : s3TestResult && s3TestResult.success
                    ? 'bg-green-50 border border-green-200'
                    : 'bg-yellow-100 border border-yellow-400',
              ]"
            >
              <div class="flex items-center justify-between mb-2">
                <p
                  :class="[
                    s3TestResult && !s3TestResult.success
                      ? 'text-red-900'
                      : s3TestResult && s3TestResult.success
                        ? 'text-green-900'
                        : 'text-yellow-900',
                  ]"
                  class="text-sm font-medium"
                >
                  {{
                    t('INBOX_MGMT.STORAGE_SETTINGS.CURRENT_S3_CONFIGURATION')
                  }}
                </p>
                <div class="flex gap-2">
                  <button
                    type="button"
                    :disabled="isTestingS3"
                    class="inline-flex items-center min-w-0 gap-2 transition-all duration-200 ease-in-out border-0 rounded-lg outline-1 outline disabled:opacity-50 bg-n-brand text-white hover:enabled:brightness-110 focus-visible:brightness-110 outline-transparent h-10 px-4 text-sm font-medium justify-center"
                    @click="onTestS3Config"
                  >
                    {{ t('INBOX_MGMT.STORAGE_SETTINGS.TEST_S3_CONNECTION') }}
                  </button>
                  <button
                    type="button"
                    class="inline-flex items-center min-w-0 gap-2 transition-all duration-200 ease-in-out border-0 rounded-lg outline-1 outline disabled:opacity-50 bg-n-brand text-white hover:enabled:brightness-110 focus-visible:brightness-110 outline-transparent h-10 px-4 text-sm font-medium justify-center"
                    @click="
                      () => {
                        showS3Form = true;
                        s3TestResult = null;
                      }
                    "
                  >
                    {{ t('INBOX_MGMT.STORAGE_SETTINGS.EDIT_CONFIGURATION') }}
                  </button>
                </div>
              </div>
              <div class="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span
                    :class="[
                      s3TestResult && !s3TestResult.success
                        ? 'text-red-700'
                        : s3TestResult && s3TestResult.success
                          ? 'text-green-700'
                          : 'text-yellow-700',
                    ]"
                    class="font-medium"
                    >Bucket:</span
                  >
                  <span
                    :class="[
                      s3TestResult && !s3TestResult.success
                        ? 'text-red-900'
                        : s3TestResult && s3TestResult.success
                          ? 'text-green-900'
                          : 'text-yellow-900',
                    ]"
                    class="ml-2"
                    >{{ s3Config.s3_bucket_name }}</span
                  >
                </div>
                <div>
                  <span
                    :class="[
                      s3TestResult && !s3TestResult.success
                        ? 'text-red-700'
                        : s3TestResult && s3TestResult.success
                          ? 'text-green-700'
                          : 'text-yellow-700',
                    ]"
                    class="font-medium"
                    >Region:</span
                  >
                  <span
                    :class="[
                      s3TestResult && !s3TestResult.success
                        ? 'text-red-900'
                        : s3TestResult && s3TestResult.success
                          ? 'text-green-900'
                          : 'text-yellow-900',
                    ]"
                    class="ml-2"
                    >{{ s3Config.s3_region }}</span
                  >
                </div>
                <div class="col-span-2">
                  <span
                    :class="[
                      s3TestResult && !s3TestResult.success
                        ? 'text-red-700'
                        : s3TestResult && s3TestResult.success
                          ? 'text-green-700'
                          : 'text-yellow-700',
                    ]"
                    class="font-medium"
                    >Endpoint:</span
                  >
                  <span
                    :class="[
                      s3TestResult && !s3TestResult.success
                        ? 'text-red-900'
                        : s3TestResult && s3TestResult.success
                          ? 'text-green-900'
                          : 'text-yellow-900',
                    ]"
                    class="ml-2 break-all"
                    >{{ s3Config.s3_endpoint }}</span
                  >
                </div>
              </div>
              <div class="mt-2">
                <div class="flex items-center gap-2">
                  <span
                    v-if="s3TestResult && s3TestResult.success"
                    class="border border-green-500 dark:border-green-400 rounded px-2 py-1 text-xs font-semibold inline-flex items-center gap-1 bg-green-500 text-white dark:bg-green-400 dark:text-green-900"
                  >
                    <svg
                      class="w-3 h-3 mr-1 text-green-500 dark:text-green-300"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                    {{ s3TestResult.message }}
                  </span>
                  <span
                    v-else-if="s3TestResult && !s3TestResult.success"
                    class="border border-red-500 dark:border-red-400 rounded px-2 py-1 text-xs font-semibold inline-flex items-center gap-1 bg-red-500 text-white dark:bg-red-400 dark:text-red-900"
                  >
                    <svg
                      class="w-3 h-3 mr-1 text-red-100 dark:text-red-900"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                      viewBox="0 0 24 24"
                    >
                      <circle cx="12" cy="12" r="10" />
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        d="M12 8v4m0 4h.01"
                      />
                    </svg>
                    {{ s3TestResult.message }}
                  </span>
                  <span
                    v-else
                    class="border border-[#23242a] rounded px-2 py-1 text-xs font-semibold inline-flex items-center gap-1 bg-[#1d1e24] text-white"
                  >
                    <svg
                      class="w-3 h-3 mr-1 text-white"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                      viewBox="0 0 24 24"
                    >
                      <circle cx="12" cy="12" r="10" />
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        d="M12 8h.01M12 16h.01"
                      />
                    </svg>
                    {{ t('INBOX_MGMT.STORAGE_SETTINGS.CONFIG_AWAITING_TEST') }}
                  </span>
                </div>
              </div>
            </div>
            <!-- Formulário de edição -->
            <form
              v-if="
                showS3Form ||
                !s3Config.s3_bucket_name ||
                !s3Config.s3_access_key_id ||
                !s3Config.s3_secret_access_key ||
                !s3Config.s3_region
              "
              class="space-y-4"
              @submit.prevent="onSaveS3Config"
            >
              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label class="block text-sm font-medium text-slate-700 mb-1"
                    >Access Key ID</label
                  >
                  <input
                    v-model="s3Config.s3_access_key_id"
                    type="text"
                    class="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                    placeholder="Access Key ID"
                  />
                </div>
                <div>
                  <label class="block text-sm font-medium text-slate-700 mb-1"
                    >Secret Access Key</label
                  >
                  <input
                    v-model="s3Config.s3_secret_access_key"
                    type="password"
                    class="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                    placeholder="Secret Access Key"
                  />
                </div>
                <div>
                  <label class="block text-sm font-medium text-slate-700 mb-1"
                    >Bucket Name</label
                  >
                  <input
                    v-model="s3Config.s3_bucket_name"
                    type="text"
                    class="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                    placeholder="Bucket Name"
                  />
                </div>
                <div>
                  <label class="block text-sm font-medium text-slate-700 mb-1"
                    >Region</label
                  >
                  <input
                    v-model="s3Config.s3_region"
                    type="text"
                    class="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                    placeholder="us-east-1"
                  />
                </div>
                <div class="md:col-span-2">
                  <label class="block text-sm font-medium text-slate-700 mb-1"
                    >Endpoint
                    <span class="text-xs text-slate-500">optional</span></label
                  >
                  <input
                    v-model="s3Config.s3_endpoint"
                    type="url"
                    class="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="https://s3.example.com"
                  />
                </div>
              </div>
              <div class="flex items-center gap-2">
                <input
                  id="force-path-style"
                  v-model="s3Config.s3_force_path_style"
                  type="checkbox"
                  class="rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                />
                <label for="force-path-style" class="text-sm text-slate-700">
                  Use path-style URLs (for MinIO/custom S3)
                </label>
              </div>
              <div class="flex gap-2">
                <button
                  type="submit"
                  :disabled="isSavingS3"
                  class="inline-flex items-center min-w-0 gap-2 transition-all duration-200 ease-in-out border-0 rounded-lg outline-1 outline disabled:opacity-50 bg-n-brand text-white hover:enabled:brightness-110 focus-visible:brightness-110 outline-transparent h-10 px-4 text-sm font-medium justify-center"
                >
                  <span class="min-w-0 truncate">Save Configuration</span>
                </button>
                <button
                  type="button"
                  class="inline-flex items-center min-w-0 gap-2 transition-all duration-200 ease-in-out border-0 rounded-lg outline-1 outline disabled:opacity-50 bg-n-brand/10 text-n-blue-text hover:enabled:bg-n-brand/20 focus-visible:bg-n-brand/20 outline-transparent h-10 px-4 text-sm font-medium justify-center"
                  @click="onCancelS3Config"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  class="inline-flex items-center min-w-0 gap-2 transition-all duration-200 ease-in-out border-0 rounded-lg outline-1 outline disabled:opacity-50 bg-red-700 text-white hover:enabled:bg-red-800 focus-visible:bg-red-800 outline-transparent h-10 px-4 text-sm font-medium justify-center"
                  @click="onClearS3Config"
                >
                  Clear S3 Configuration
                </button>
              </div>
              <div
                v-if="s3TestResult"
                :class="{
                  'text-green-500': s3TestResult.success,
                  'text-red-500': !s3TestResult.success,
                }"
              >
                <div>{{ s3TestResult.message }}</div>
                <div
                  v-if="s3TestResult.detail"
                  class="text-xs mt-1 text-slate-500 break-all"
                >
                  {{ s3TestResult.detail }}
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
    <!-- Storage Analysis Section -->
    <div
      v-if="showStorageAnalysisSection"
      class="ml-0 mr-0 pt-8 pb-4 w-full border-b border-solid border-n-weak/60 dark:border-n-weak"
    >
      <StorageAnalysis
        :inbox-id="inboxId"
        :storage-type="selectedType"
        :folder-style-status="folderStyleStatus"
        :storage-migration-status="storageMigrationStatus"
        :bucket="effectiveBucket"
        :folder-style-enabled="showFolderStyleCounters"
      />

      <div
        v-if="showFolderStyleSection"
        class="mt-6 border-t border-solid border-n-weak/60 dark:border-n-weak pt-8"
      >
        <FolderStyleMigration
          :inbox-id="inboxId"
          :folder-style-status="folderStyleStatus"
          :storage-migration-status="storageMigrationStatus"
          :existence-status="existenceStatus"
        />
      </div>
    </div>
  </div>

  <Dialog
    ref="confirmationDialogRef"
    type="alert"
    width="xl"
    :title="
      confirmationDialogState.title ||
      t('INBOX_MGMT.STORAGE_SETTINGS.CHANGE_WARNING_TITLE')
    "
    :show-cancel-button="false"
    :show-confirm-button="false"
    @close="handleConfirmationDialogClose"
  >
    <template #description>
      <div class="space-y-4 text-sm text-n-slate-11">
        <div class="space-y-1">
          <p class="mb-0">
            <span class="font-medium text-n-slate-12">
              {{ t('INBOX_MGMT.STORAGE_SETTINGS.PREVIOUS_SELECTION') }}:
            </span>
            {{ confirmationDialogState.previousSelection }}
          </p>
          <p class="mb-0">
            <span class="font-medium text-n-slate-12">
              {{ t('INBOX_MGMT.STORAGE_SETTINGS.NEW_SELECTION') }}:
            </span>
            {{ confirmationDialogState.newSelection }}
          </p>
        </div>

        <div class="space-y-2">
          <p class="mb-0 font-medium text-n-slate-12">
            {{ t('INBOX_MGMT.STORAGE_SETTINGS.IMPACT_TITLE') }}:
          </p>
          <ul class="mb-0 list-disc ps-5 space-y-1">
            <li
              v-for="impactItem in confirmationDialogState.impactItems"
              :key="impactItem"
            >
              {{ impactItem }}
            </li>
          </ul>
        </div>

        <p class="mb-0 font-medium text-n-slate-12">
          {{ t('INBOX_MGMT.STORAGE_SETTINGS.CONFIRM_CONTINUE') }}
        </p>
        <p
          v-if="confirmationDialogState.confirmActionText"
          class="mb-0 text-n-slate-12 whitespace-pre-line"
        >
          {{ confirmationDialogState.confirmActionText }}
        </p>
      </div>
    </template>

    <template #footer>
      <div class="flex items-center justify-between w-full gap-3">
        <button
          type="button"
          class="w-full inline-flex items-center min-w-0 gap-2 transition-all duration-100 ease-out border-0 rounded-lg outline-1 outline disabled:opacity-50 bg-n-slate-9/10 text-n-slate-12 hover:enabled:bg-n-slate-9/20 focus-visible:bg-n-slate-9/20 outline-transparent h-10 px-4 text-sm font-medium active:enabled:scale-[0.98] justify-center"
          @click="confirmationDialogRef?.close()"
        >
          <span class="min-w-0 truncate">{{ t('DIALOG.BUTTONS.CANCEL') }}</span>
        </button>
        <button
          type="button"
          class="w-full inline-flex items-center min-w-0 gap-2 transition-all duration-100 ease-out border-0 rounded-lg outline-1 outline disabled:opacity-50 bg-n-ruby-9 text-white hover:enabled:bg-n-ruby-10 focus-visible:bg-n-ruby-10 outline-transparent h-10 px-4 text-sm font-medium active:enabled:scale-[0.98] justify-center"
          @click="handleConfirmationDialogConfirm"
        >
          <span class="min-w-0 truncate">
            {{ t('DIALOG.BUTTONS.CONFIRM') }}
          </span>
        </button>
      </div>
    </template>
  </Dialog>
</template>
